// Shared browser/Edge taxonomy. Only categorical metadata, internal IDs and counts.
export const growthEvents = {
  page_viewed: "growth_page_viewed",
  primary_cta_clicked: "growth_primary_cta_clicked",
  demo_video_clicked: "growth_demo_video_clicked",
  pricing_viewed: "growth_pricing_viewed",
  plan_selected: "growth_plan_selected",
  checkout_started: "growth_checkout_started",
  trial_started: "growth_trial_started",
  subscription_created: "growth_subscription_created",
  subscription_trialing: "growth_subscription_trialing",
  subscription_paid: "growth_subscription_paid",
  subscription_cancel_requested: "growth_subscription_cancel_requested",
  subscription_cancelled: "growth_subscription_cancelled",
  subscription_expired: "growth_subscription_expired",
  subscription_reactivated: "growth_subscription_reactivated",
  subscription_plan_changed: "growth_subscription_plan_changed",
  onboarding_started: "growth_onboarding_started",
  onboarding_step_completed: "growth_onboarding_step_completed",
  onboarding_completed: "growth_onboarding_completed",
  app_viewed: "growth_app_viewed",
  book_enabled: "growth_book_enabled",
  book_disabled: "growth_book_disabled",
  existing_book_upload_started: "growth_existing_book_upload_started",
  existing_book_uploaded: "growth_existing_book_uploaded",
  existing_book_upload_failed: "growth_existing_book_upload_failed",
  social_connect_started: "growth_social_connect_started",
  social_connected: "growth_social_connected",
  social_connect_failed: "growth_social_connect_failed",
  social_disconnected: "growth_social_disconnected",
  first_social_connected: "growth_first_social_connected",
  content_generation_started: "growth_content_generation_started",
  content_generated: "growth_content_generated",
  content_generation_failed: "growth_content_generation_failed",
  image_generation_started: "growth_image_generation_started",
  image_generated: "growth_image_generated",
  image_generation_failed: "growth_image_generation_failed",
  content_regenerated: "growth_content_regenerated",
  content_edited: "growth_content_edited",
  content_approved: "growth_content_approved",
  week_review_viewed: "growth_week_review_viewed",
  week_approved: "growth_week_approved",
  calendar_viewed: "growth_calendar_viewed",
  calendar_filter_used: "growth_calendar_filter_used",
  calendar_item_moved: "growth_calendar_item_moved",
  content_scheduled: "growth_content_scheduled",
  content_unscheduled: "growth_content_unscheduled",
  content_rescheduled: "growth_content_rescheduled",
  batch_generation_started: "growth_batch_generation_started",
  batch_generation_completed: "growth_batch_generation_completed",
  autopilot_enabled: "growth_autopilot_enabled",
  autopilot_disabled: "growth_autopilot_disabled",
  publish_started: "growth_publish_started",
  target_published: "growth_target_published",
  first_post_published: "growth_first_post_published",
  publish_failed: "growth_publish_failed",
  publish_blocked_subscription: "growth_publish_blocked_subscription",
  publish_blocked_social_account: "growth_publish_blocked_social_account",
  analytics_viewed: "growth_analytics_viewed",
  metrics_refreshed: "growth_metrics_refreshed",
  ai_operation_completed: "growth_ai_operation_completed",
  ai_operation_failed: "growth_ai_operation_failed",
  quota_blocked: "growth_quota_blocked",
  upgrade_clicked: "growth_upgrade_clicked",
  content_create_started: "growth_content_create_started",
  book_filter_used: "growth_book_filter_used",
  week_generated: "growth_week_generated",
  autopilot_enterprise_contact_clicked:
    "growth_autopilot_enterprise_contact_clicked",
  autopilot_inquiry_submitted: "growth_autopilot_inquiry_submitted",
} as const;
export type GrowthEvent = typeof growthEvents[keyof typeof growthEvents];
export type GrowthProperties = Partial<
  Record<GrowthProperty, string | number | boolean | null>
>;
export const growthPropertyKeys = [
  "authenticated",
  "has_growth_subscription",
  "growth_plan",
  "plan",
  "current_plan",
  "previous_plan",
  "new_plan",
  "selected_plan",
  "billing_interval",
  "displayed_price",
  "regular_price",
  "currency",
  "founders_offer",
  "trial_days",
  "subscription_status",
  "is_trial",
  "cta_location",
  "destination",
  "location",
  "source_surface",
  "app_surface",
  "step",
  "step_number",
  "approval_mode",
  "book_source",
  "selected_networks_count",
  "connected_accounts_count",
  "connected_social_accounts_count",
  "active_books_count",
  "active_books_count_after",
  "connected_accounts_count_after",
  "has_social_destination",
  "book_id",
  "content_item_id",
  "target_id",
  "content_type",
  "provider",
  "network",
  "goal",
  "content_angle",
  "generation_mode",
  "used_image_generation",
  "generation_duration_ms",
  "duration_ms",
  "safe_error_category",
  "generation_stage",
  "regeneration_type",
  "edited_fields_count",
  "target_count",
  "bulk_action",
  "content_count",
  "book_count",
  "network_count",
  "view_mode",
  "mode",
  "filter_type",
  "days_delta",
  "scheduling_mode",
  "period",
  "requested_content_count",
  "generated_content_count",
  "failed_content_count",
  "scope",
  "posting_frequency",
  "reason_category",
  "quota_type",
  "usage_percent_bucket",
  "upgrade_target",
  "reason",
  "operation_type",
  "success",
  "provider_category",
  "input_tokens",
  "output_tokens",
  "is_first_payment",
  "amount",
  "subscription_id",
  "cancel_at_period_end",
  "days_since_trial_started",
  "days_since_onboarding_completed",
  "posts_updated_count",
  "file_type",
  "processing_success",
  "book_filter_present",
  "network_filter_present",
  "date_range_type",
  "angle",
  "count",
  "tier",
  "billing_cycle",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "catalog_size",
  "amount_unit",
  "author_identity_present",
  "days",
] as const;
export type GrowthProperty = typeof growthPropertyKeys[number];
const allowed = new Set<string>(growthPropertyKeys);
export function safeGrowthProperties(
  properties: Record<string, unknown>,
): GrowthProperties {
  return Object.fromEntries(
    Object.entries(properties).filter(([key, value]) =>
      allowed.has(key) && (value === null || typeof value === "boolean" ||
        (typeof value === "number" && Number.isFinite(value)) ||
        (typeof value === "string" && value.length <= 200 &&
          !/[\r\n]/.test(value)))
    ),
  );
}

const personKeys = new Set([
  "growth_customer",
  "growth_plan",
  "growth_subscription_status",
  "growth_is_trial",
  "growth_onboarding_completed",
  "growth_activation_completed",
  "growth_active_books_count",
  "growth_connected_accounts_count",
  "growth_approval_mode",
  "growth_first_trial_at",
  "growth_first_paid_at",
  "growth_first_publish_at",
]);
export function safeGrowthPersonProperties(value: unknown) {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value).filter(([key, v]) =>
      personKeys.has(key) &&
      (v === null || typeof v === "boolean" ||
        (typeof v === "number" && Number.isFinite(v)) ||
        (typeof v === "string" && v.length <= 100 && !/[\r\n]/.test(v)))
    ),
  );
}

// Run for SDK automatic events too: OAuth query/hash values must never reach analytics.
export function sanitizeGrowthBrowserEvent<
  T extends { event: string; properties?: Record<string, unknown> },
>(event: T | null, path: string): T | null {
  if (!event || !path.startsWith("/growth-autopilot")) return event;
  if (["$autocapture", "$snapshot", "$exception"].includes(event.event)) {
    return null;
  }
  const sanitizeUrls = (properties: Record<string, unknown>) => {
    for (
      const key of [
        "$current_url",
        "$referrer",
        "$initial_current_url",
        "$initial_referrer",
      ]
    ) {
      if (typeof properties[key] !== "string") continue;
      try {
        const url = new URL(properties[key] as string);
        properties[key] = url.origin + url.pathname;
      } catch {
        delete properties[key];
      }
    }
  };
  if (event.properties) {
    sanitizeUrls(event.properties);
    for (const key of ["$set", "$set_once"]) {
      const properties = event.properties[key];
      if (properties && typeof properties === "object") {
        sanitizeUrls(properties as Record<string, unknown>);
      }
    }
  }
  return event;
}
